import type { GetHealthDashboardResponse } from "@wallyo/contracts/console/dashboard";
import type { ReactNode } from "react";
import { t } from "../../i18n";
import { formatNumber, formatPercent } from "../../i18n/format";
import { alertView, generatedLine, queueRows, tradeLabel, waitedFor } from "./dashboard-model";

/** A count, with its share when there is one: "31 · 74 %". */
const share = (count: number, ratio: number | null) =>
  ratio === null ? formatNumber(count) : `${formatNumber(count)} · ${formatPercent(ratio)}`;

function Card({ title, big, unit, rows }: { title: string; big: ReactNode; unit?: string; rows: [string, string][] }) {
  return (
    <div className="card">
      <h2>{title}</h2>
      <div className="big">
        {big}
        {unit ? <small>{unit}</small> : null}
      </div>
      <ul className="kv">
        {rows.map(([label, value]) => (
          <li key={label}>
            <span>{label}</span>
            <span>{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Tableau de bord: alerts first, then the day's numbers, coverage and the work queues. */
export function DashboardView({ dashboard: d }: { dashboard: GetHealthDashboardResponse }) {
  const hours = formatNumber(d.firstReplyWindowMinutes / 60, { maximumFractionDigits: 1 });
  const alerts = d.alerts.map(alertView);
  const queues = queueRows(d.queues);
  return (
    <>
      <p className="foot" data-generated="">
        {generatedLine(d.generatedAt)}
      </p>

      {alerts.length > 0 ? (
        <section className="alerts" aria-label={t("dashboard.alertsAria")}>
          {alerts.map((alert, index) => (
            <div
              className={`alert ${alert.tone === "bad" ? "bad" : ""}`}
              key={index}
              data-alert={d.alerts[index]!.kind}
            >
              <span className={`pill ${alert.tone}`}>{alert.tag}</span>
              <span>{alert.text}</span>
              {alert.href ? (
                <a className="go" href={alert.href}>
                  {alert.linkLabel}
                </a>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      <section className="grid4">
        <Card
          title={t("dashboard.card.demand")}
          big={formatNumber(d.demand.jobsPosted)}
          rows={[
            [
              t("dashboard.card.demand.answered", { hours }),
              share(d.demand.answeredInWindow, d.demand.answeredInWindowShare),
            ],
            [t("dashboard.card.demand.unanswered"), formatNumber(d.demand.unanswered)],
            [t("dashboard.card.demand.expired"), formatNumber(d.demand.expired)],
          ]}
        />
        <Card
          title={t("dashboard.card.introductions")}
          big={formatNumber(d.introductions.introductionsMade)}
          rows={[
            [t("dashboard.card.introductions.quotes"), formatNumber(d.introductions.quotesSent)],
            [
              t("dashboard.card.introductions.median"),
              d.introductions.medianFirstResponseMinutes === null
                ? "—"
                : waitedFor(d.introductions.medianFirstResponseMinutes),
            ],
            [t("dashboard.card.introductions.cancelled"), formatNumber(d.introductions.cancelledBeforeIntroduction)],
          ]}
        />
        <Card
          title={t("dashboard.card.pros")}
          big={formatNumber(d.pros.activeVerified)}
          unit={t("dashboard.card.pros.unit")}
          rows={[
            [t("dashboard.card.pros.new"), formatNumber(d.pros.newInLast7Days)],
            [t("dashboard.card.pros.waiting"), formatNumber(d.pros.waitingForVerification)],
            [t("dashboard.card.pros.suspended"), formatNumber(d.pros.suspended)],
          ]}
        />
        {d.money ? (
          <Card
            title={t("dashboard.card.money")}
            big={formatNumber(d.money.feesCollectedGnf)}
            unit="GNF"
            rows={[
              [t("dashboard.card.money.failed"), formatNumber(d.money.failedOrangeMoneyPayments)],
              [t("dashboard.card.money.approvals"), formatNumber(d.money.pendingApprovals)],
              [t("dashboard.card.money.subscriptions"), formatNumber(d.money.activeMonthlySubscriptions)],
            ]}
          />
        ) : null}
      </section>

      <section className="split">
        <div className="card">
          <h2>{t("dashboard.coverage")}</h2>
          {d.coverage.length === 0 ? (
            <p className="foot">{t("dashboard.coverage.empty")}</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("dashboard.col.trade")}</th>
                    <th>{t("dashboard.col.commune")}</th>
                    <th className="num">{t("dashboard.col.requests")}</th>
                    <th className="num">{t("dashboard.col.pros")}</th>
                    <th>{t("dashboard.col.answered", { hours })}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.coverage.map((row) => (
                    <tr key={`${row.category}-${row.city}`}>
                      <td>{tradeLabel(row.category)}</td>
                      <td>{row.city}</td>
                      <td className="num">{formatNumber(row.requests)}</td>
                      <td className="num">{formatNumber(row.availablePros)}</td>
                      <td>
                        <div className="meter">
                          <i>
                            <b
                              className={row.answeredInWindowShare < 0.5 ? "low" : ""}
                              style={{ width: `${row.answeredInWindowShare * 100}%` }}
                            />
                          </i>
                          {formatPercent(row.answeredInWindowShare)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="foot">{t("dashboard.coverageFoot")}</p>
        </div>
        <div className="card">
          <h2>{t("dashboard.queues")}</h2>
          <div className="rows">
            {queues.length === 0 ? <p className="foot">{t("dashboard.noQueues")}</p> : null}
            {queues.map((queue) => (
              <a href={queue.href} key={queue.key} data-queue={queue.key}>
                <span>
                  {queue.label}
                  {queue.meta ? <span className="meta"> · {queue.meta}</span> : null}
                </span>
                <span className={`pill ${queue.tone}`}>{formatNumber(queue.count)}</span>
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
